import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.find-index.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.array.from.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/es.array.join.js";
import { useState, useRef, useEffect } from 'react';
import focusableSelectors from 'focusable-selectors';
import { KEY } from 'utils/ui/constants';
import usePrevious from 'hooks/usePrevious';
/**
 * Returns whether an element has a size bigger than 0 pixels
 * in any direction, and isn't set to `display: none`.
 */

function doesElementHaveDimensions(element) {
  return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
}
var TABLE_HIGHLIGHT_KEYS = [KEY.ARROW_UP, KEY.ARROW_DOWN, KEY.ARROW_LEFT, KEY.ARROW_RIGHT, KEY.PAGE_UP, KEY.PAGE_DOWN, KEY.HOME, KEY.END];
function findNextVisibleCell(_ref) {
  var elementList = _ref.elementList,
    startIndex = _ref.startIndex,
    direction = _ref.direction;
  var cellCount = elementList.length;
  function checkCellVisibility(index) {
    var cellElement = elementList[index];
    if (cellElement && doesElementHaveDimensions(cellElement)) {
      // If cell is visible, return it
      return cellElement;
    } else {
      // Otherwise check the neighbouring cell instead (left or right)
      var newIndex = index + direction;
      if (newIndex < 0 || newIndex > cellCount - 1) {
        // Bail out if we've reached the edge of the grid
        return null;
      }
      return checkCellVisibility(newIndex);
    }
  }
  return checkCellVisibility(startIndex);
}
function useGridNavigation(_ref2) {
  var rowIds = _ref2.rowIds,
    getInitialHighlightedRow = _ref2.getInitialHighlightedRow,
    _ref2$role = _ref2.role,
    role = _ref2$role === void 0 ? 'gridcell' : _ref2$role;
  var highlightedRowCellRefs = useRef([]);
  var shouldHandleFocus = useRef(false);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    highlightedRowId = _useState2[0],
    setHighlightedRowId = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    highlightedCellIndex = _useState4[0],
    setHighlightedCellIndex = _useState4[1];
  var previousHighlightedCellIndex = usePrevious(highlightedCellIndex);
  if (highlightedRowId === undefined) {
    var fallbackRowId = getInitialHighlightedRow();
    if (fallbackRowId !== null && fallbackRowId !== undefined) {
      setHighlightedRowId(fallbackRowId);
    }
  }
  var highlightedRowIndex = rowIds.findIndex(function (itemId) {
    return highlightedRowId === itemId;
  });
  var rowCount = rowIds.length;
  function getIsRowHighlighted(id) {
    return id === highlightedRowId;
  }
  function getRowProps(id) {
    return {
      onMouseEnter: function onMouseEnter() {
        return setHighlightedRowId(id);
      }
    };
  }
  function getCellProps(rowId, cellIndex) {
    var props = {
      role: role,
      onFocus: function onFocus() {
        return setHighlightedCellIndex(cellIndex);
      }
    };
    // If the row is highlighted, collect refs of its cells
    if (getIsRowHighlighted(rowId)) {
      props.ref = function (ref) {
        highlightedRowCellRefs.current[cellIndex] = ref;
      };
    }
    return props;
  }
  function handleGridNavigationKeys(event) {
    if (TABLE_HIGHLIGHT_KEYS.includes(event.key)) {
      event.preventDefault();
      shouldHandleFocus.current = true;
      var columnCount = highlightedRowCellRefs.current.length;
      switch (event.key) {
        case KEY.ARROW_UP:
          setHighlightedRowId(rowIds[(highlightedRowIndex || rowCount) - 1]);
          break;
        case KEY.ARROW_DOWN:
          setHighlightedRowId(rowIds[(highlightedRowIndex + 1) % rowCount]);
          break;
        case KEY.ARROW_LEFT:
          setHighlightedCellIndex(function (currentCellIndex) {
            return currentCellIndex > 0 ? currentCellIndex - 1 : currentCellIndex;
          });
          break;
        case KEY.ARROW_RIGHT:
          setHighlightedCellIndex(function (currentCellIndex) {
            return currentCellIndex < columnCount - 1 ? currentCellIndex + 1 : currentCellIndex;
          });
          break;
        case KEY.PAGE_UP:
          setHighlightedRowId(rowIds[0]);
          break;
        case KEY.PAGE_DOWN:
          setHighlightedRowId(rowIds[rowCount - 1]);
          break;
        case KEY.HOME:
          setHighlightedCellIndex(0);
          if (event.ctrlKey) {
            setHighlightedRowId(rowIds[0]);
          }
          break;
        case KEY.END:
          setHighlightedCellIndex(columnCount - 1);
          if (event.ctrlKey) {
            setHighlightedRowId(rowIds[rowCount - 1]);
          }
          break;
      }
    }
  }
  useEffect(function () {
    // We only move focus when the keyboard was used to navigate the grid
    if (shouldHandleFocus.current) {
      var cellElement = findNextVisibleCell({
        elementList: highlightedRowCellRefs.current,
        startIndex: highlightedCellIndex,
        direction: !previousHighlightedCellIndex || highlightedCellIndex > previousHighlightedCellIndex ? 1 : -1
      });
      if (cellElement) {
        // Find focusable, visible elements within the highlighted cell
        var focusableElements = Array.from(cellElement.querySelectorAll(focusableSelectors.join(','))).filter(doesElementHaveDimensions);
        if (focusableElements.length) {
          // If the cell contains focusable elements, move focus to the
          // first one of them
          focusableElements[0].focus();
        } else {
          // Otherwise focus the cell itself
          cellElement.focus();
        }
      }
      shouldHandleFocus.current = false;
    }
  }, [highlightedRowId, highlightedCellIndex, previousHighlightedCellIndex]);
  return {
    highlightedRowId: highlightedRowId,
    setHighlightedRowId: setHighlightedRowId,
    highlightedRowIndex: highlightedRowIndex,
    getIsRowHighlighted: getIsRowHighlighted,
    getRowProps: getRowProps,
    getCellProps: getCellProps,
    handleGridNavigationKeys: handleGridNavigationKeys
  };
}
export default useGridNavigation;